import * as React from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteShell } from "@/components/SiteShell";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/contexts/AuthContext";
import { DICTS } from "@/lib/i18n";
import tierBannerImage from "@/assets/mirna-social-frame-banner.png.asset.json";
import { ArrowLeft, ArrowRight, Sparkles, TrendingUp, Crown, CheckCircle2, HelpCircle, Lightbulb, Gift, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { getTierContent } from "@/lib/ambassadors-tier-content";
import { smartBackOnClick } from "@/lib/smartBack";
import { useExclusiveDetails } from "@/lib/useExclusiveDetails";
import { getPublicTierRewards } from "@/utils/ambassadorTierPublic.functions";
import { RewardConditionsCard } from "@/components/RewardConditionsCard";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";

const TIER_KEYS = ["lover", "ambassador", "superstar"] as const;
type TierKey = (typeof TIER_KEYS)[number];

export function isTierKey(v: string): v is TierKey {
  return (TIER_KEYS as readonly string[]).includes(v);
}

export const Route = createFileRoute("/ambassadors/tier/$key")({
  beforeLoad: ({ params }) => {
    if (!isTierKey(params.key)) throw notFound();
  },
  loader: ({ params }) => {
    const k = isTierKey(params.key) ? params.key : "lover";
    return getPublicTierRewards({ data: { tierUiKey: k } });
  },
  head: ({ params }) => {
    const k = isTierKey(params.key) ? params.key : "lover";
    const name = DICTS.en[`ambassadors.tiers.${k}.name` as keyof typeof DICTS.en];
    const short = DICTS.en[`ambassadors.tiers.${k}.short` as keyof typeof DICTS.en];
    const url = `${PUBLIC_SITE_ORIGIN}/ambassadors/tier/${k}`;
    return {
      meta: [
        { title: `${name} — Lecmo Ambassadors` },
        { name: "description", content: short },
        { property: "og:title", content: `${name} — Lecmo Ambassadors` },
        { property: "og:description", content: short },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: TierDetailPage,
});

const ICONS: Record<TierKey, React.ComponentType<{ className?: string }>> = {
  lover: Sparkles,
  ambassador: TrendingUp,
  superstar: Crown,
};

function TierDetailPage() {
  const { key } = Route.useParams();
  const tierKey = isTierKey(key) ? key : "lover";
  const { t, locale, formatPrice, currency } = useLocale();
  const { rules: liveRules, tier: dbTier, pointValueAed } = Route.useLoaderData();
  const { user } = useAuth();
  const Icon = ICONS[tierKey];
  const ArrowBack = locale === "ar" ? ArrowRight : ArrowLeft;

  // ملاحظة: لا نفرض scrollTo(0) — الراوتر يفعّل scrollRestoration عالمياً،
  // فيقفز للأعلى عند التنقّل الأماميّ ويستعيد الموضع عند زرّ الرجوع.


  const content = getTierContent(locale, tierKey);

  const ctaButton = (() => {
    if (tierKey === "superstar") {
      return (
        <Button asChild size="lg" className="w-full sm:w-auto">
          <Link to="/ambassadors" onClick={smartBackOnClick("/ambassadors")}>
            {t("ambassadors.tier_page.cta_superstar")}
          </Link>
        </Button>
      );
    }
    // lover: مسجَّل → «شارك رابطك» يأخذه للوحة السفير. زائر → «أنشئ حسابك».
    if (tierKey === "lover") {
      return (
        <Button asChild size="lg" className="w-full sm:w-auto">
          <Link
            to={user ? "/account/media" : "/auth"}
            search={user ? undefined : { mode: "signup", redirect: "/account/media" }}
          >
            {user ? t("ambassadors.tier_page.cta_share_link") : t("ambassadors.tier_page.cta_lover")}
          </Link>
        </Button>
      );
    }

    if (user) {
      return (
        <Button asChild size="lg" className="w-full sm:w-auto">
          <Link to="/account/ambassador">
            {t("ambassadors.tier_page.cta_ambassador")}
          </Link>
        </Button>
      );
    }
    return (
      <Button asChild size="lg" className="w-full sm:w-auto">
        <Link to="/auth" search={{ mode: "signup", redirect: "/account/ambassador" }}>
          {t("ambassadors.tier_page.cta_lover")}
        </Link>
      </Button>
    );
  })();

  return (
    <SiteShell>
      <PageHero
        title={t(`ambassadors.tiers.${tierKey}.name`)}
        subtitle={t(`ambassadors.tiers.${tierKey}.short`)}
        image={tierBannerImage.url}
        alt={t(`ambassadors.tiers.${tierKey}.name`)}
        pageKey="ambassadors-tier"
      />

      <section className="mx-auto max-w-3xl px-4 py-12 md:py-16">


        {/* بطاقة التعريف */}
        <div className="rounded-none border border-primary/40 bg-card p-6 md:p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </span>
            <div>
              <h1 className="t-h3">{t(`ambassadors.tiers.${tierKey}.name`)}</h1>
              <span className="text-xs text-muted-foreground">
                {t(`ambassadors.tiers.${tierKey}.tag`)}
              </span>
            </div>
          </div>
          <p className="text-sm md:text-base leading-relaxed text-foreground/90">
            {content.intro}
          </p>
        </div>

        {/* بطاقة الأرباح الحيّة من ambassador_reward_rules */}
        <div className="mb-8">
          <RewardConditionsCard
            variant="public"
            tier={dbTier}
            rules={liveRules}
            locale={locale}
            t={t}
            formatPrice={formatPrice}
            currency={currency}
            pointValueAed={pointValueAed}
          />
        </div>

        {/* كيفيّة الوصول */}
        <div className="rounded-none border border-border/60 bg-card p-6 md:p-8 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            <h2 className="t-h4">{content.qualifyTitle}</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground/85 text-center">{content.qualifyBody}</p>
        </div>

        {/* المزايا التفصيليّة */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Gift className="h-5 w-5 text-primary" />
            <h2 className="t-h4">{content.benefitsTitle}</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {content.benefits.map((b, i) => (
              <div
                key={i}
                className="rounded-none border border-border/60 bg-card p-4"
              >
                <h3 className="text-sm font-semibold mb-1.5 text-foreground">{b.title}</h3>
                <p className="text-xs leading-relaxed text-muted-foreground">{b.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* كيف تستفيد */}
        <div className="rounded-none border border-border/60 bg-muted/30 p-6 md:p-8 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="h-5 w-5 text-primary" />
            <h2 className="t-h4">{content.howToTitle}</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground/85">{content.howToBody}</p>
          {content.howToSteps && content.howToSteps.length > 0 && (
            <ol className="mt-4 space-y-2.5">
              {content.howToSteps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {i + 1}
                  </span>
                  <span className="text-sm leading-relaxed text-foreground/85">{step}</span>
                </li>
              ))}
            </ol>
          )}
          {content.howToClosing && (
            <p className="mt-4 border-t border-border/60 pt-3 text-sm leading-relaxed text-foreground/85">
              {content.howToClosing}
            </p>
          )}
        </div>

        {/* أسئلة شائعة */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="h-5 w-5 text-primary" />
            <h2 className="t-h4">{content.faqTitle}</h2>
          </div>
          <div ref={useExclusiveDetails<HTMLDivElement>()} className="space-y-3">
            {content.faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-none border border-border/60 bg-card p-4 open:border-primary/40 open:bg-muted/30 transition-colors [&>summary::-webkit-details-marker]:hidden [&>summary]:marker:content-none"
              >
                <summary className="cursor-pointer list-none text-sm font-medium text-foreground flex items-center gap-3">
                  <span className="flex-1">{f.q}</span>
                  <ChevronDown
                    className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180"
                    aria-hidden
                  />
                </summary>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="rounded-none border border-primary/40 bg-primary/5 p-6 md:p-8 mb-10 text-center">
          <p className="text-sm md:text-base leading-relaxed text-foreground/90 mb-4">
            {user && tierKey !== "superstar"
              ? t("ambassadors.tier_page.encourage_member")
              : t(`ambassadors.tiers.${tierKey}.encourage`)}
          </p>
          <div className="flex justify-center">{ctaButton}</div>
        </div>


        {/* روابط الشرائح الأخرى */}
        <div className="grid gap-3 sm:grid-cols-2">
          {TIER_KEYS.filter((k) => k !== tierKey).map((k) => {
            const OtherIcon = ICONS[k];
            return (
              <Link
                key={k}
                to="/ambassadors/tier/$key"
                params={{ key: k }}
                onClick={smartBackOnClick(`/ambassadors/tier/${k}`)}
                className={cn(
                  "rounded-none border border-border/60 bg-card p-4",
                  "flex items-center gap-3 hover:bg-muted/40 transition",
                )}
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <OtherIcon className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">
                    {t(`ambassadors.tiers.${k}.name`)}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {t(`ambassadors.tiers.${k}.tag`)}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </SiteShell>
  );
}
